/**
 * CitationContext — bidirectional hover/active state between inline citation
 * chips and source reference rows.
 *
 * Both MarkdownMessage (chips) and Sources (rows) read/write the same
 * `activeCitation` string so hovering or focusing either end highlights its
 * counterpart. Null means no citation is active.
 *
 * Usage: wrap the message-level subtree (ChatMessage) with <CitationProvider>.
 * Both MarkdownMessage and Sources are inside that subtree.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface CitationContextValue {
  /** Citation id string currently hovered/focused (e.g. "1" or "1.2"), or null. */
  activeCitation: string | null;
  /** Called with the citation id string when entering hover/focus. */
  setActiveCitation: (id: string | null) => void;
  /** Called when leaving hover/focus. */
  clearActiveCitation: () => void;
  /**
   * Chip CLICK: ask the sources section to reveal itself (it renders collapsed
   * by default), then scroll to and flash the citation's card. `tick` makes
   * repeated clicks on the same id observable.
   */
  revealCitation: (id: string) => void;
  /** Latest reveal request, consumed by Sources. */
  revealRequest: { id: string; tick: number } | null;
  /**
   * Chip CLICK, part two (NotebookLM binding model): open the cited document's
   * in-app viewer directly at the cited passage. `statement` is the answer
   * text preceding the clicked marker, so the routing can pick the passage
   * that supports THAT statement. Wired by ChatMessage to the shared drawer
   * routing; undefined when no viewer routing is available.
   */
  openDocument?: (documentId: string, statement?: string) => void;
}

const CitationContext = createContext<CitationContextValue>({
  activeCitation: null,
  setActiveCitation: () => undefined,
  clearActiveCitation: () => undefined,
  revealCitation: () => undefined,
  revealRequest: null,
  openDocument: undefined,
});

export const CitationProvider: React.FC<{
  children: React.ReactNode;
  /** Opens the in-app viewer for a cited document (chip click jump target). */
  onOpenDocument?: (documentId: string, statement?: string) => void;
}> = ({ children, onOpenDocument }) => {
  const [activeCitation, setActive] = useState<string | null>(null);
  const [revealRequest, setRevealRequest] = useState<{ id: string; tick: number } | null>(null);

  const setActiveCitation = useCallback((id: string | null) => {
    setActive(id);
  }, []);

  const clearActiveCitation = useCallback(() => {
    setActive(null);
  }, []);

  const revealCitation = useCallback((id: string) => {
    setRevealRequest((prev) => ({ id, tick: (prev?.tick ?? 0) + 1 }));
  }, []);

  const value = useMemo(
    () => ({
      activeCitation,
      setActiveCitation,
      clearActiveCitation,
      revealCitation,
      revealRequest,
      openDocument: onOpenDocument,
    }),
    [activeCitation, setActiveCitation, clearActiveCitation, revealCitation, revealRequest, onOpenDocument]
  );

  return <CitationContext.Provider value={value}>{children}</CitationContext.Provider>;
};

export const useCitationContext = (): CitationContextValue => useContext(CitationContext);
